import React, { useRef, useState } from "react"
import { Form, Button, Spinner } from "react-bootstrap"

const GallerySectionForm = ({
  fields,
  onInputChange,
  upload,
  showAlert,
  isUploading,
  validationErrors,
  setValidationErrors,
  section = "gallery",
}) => {
  const fileInputRef = useRef(null)
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 })

  const handleInputChange = (key, value) => {
    onInputChange(section, key, value)
    if (validationErrors[`${section}.${key}`]) {
      setValidationErrors((prev) => ({
        ...prev,
        [`${section}.${key}`]: undefined,
      }))
    }
  }

  const pictureUrls = (fields.pictureUrls || []).filter(Boolean)
  const isUploadingMultiple = uploadProgress.total > 0

  const handleFilesSelect = async (e) => {
    const files = e.target.files ? [...e.target.files] : []
    if (files.length === 0) return

    const urls = [...pictureUrls]
    setUploadProgress({ current: 0, total: files.length })

    for (let i = 0; i < files.length; i++) {
      setUploadProgress((prev) => ({ ...prev, current: i + 1 }))
      try {
        const result = await upload(files[i])
        if (result?.url) {
          urls.push(result.url)
          handleInputChange("pictureUrls", [...urls])
        }
      } catch (err) {
        console.error("Gallery image upload failed:", err)
        showAlert(`Failed to upload "${files[i].name}".`, "danger")
      }
    }

    setUploadProgress({ current: 0, total: 0 })
    if (fileInputRef.current) fileInputRef.current.value = ""
    if (urls.length > pictureUrls.length) {
      showAlert("Images uploaded.", "success")
    }
  }

  const handleRemoveImage = (index) => {
    const updatedUrls = [...pictureUrls]
    updatedUrls.splice(index, 1)
    handleInputChange("pictureUrls", updatedUrls.length ? updatedUrls : [""])
  }

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>
          Heading {fields.isVisible && <span className="text-danger">*</span>}
        </Form.Label>
        <Form.Control
          type="text"
          placeholder={`Heading ${fields.isVisible ? "*" : ""}`}
          value={fields.heading || ""}
          onChange={(e) => handleInputChange("heading", e.target.value)}
          isInvalid={!!validationErrors[`${section}.heading`]}
        />
        {validationErrors[`${section}.heading`] && (
          <Form.Control.Feedback type="invalid">
            {validationErrors[`${section}.heading`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Upload images</Form.Label>
        <Form.Control
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFilesSelect}
          disabled={isUploading || isUploadingMultiple}
        />
        {(isUploading || isUploadingMultiple) && (
          <div className="mt-2 d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span className="text-muted">
              {isUploadingMultiple
                ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}...`
                : "Uploading..."}
            </span>
          </div>
        )}
      </Form.Group>

      {pictureUrls.length > 0 && (
        <>
          <Form.Label className="d-block">Preview</Form.Label>
          <div
            className="d-flex flex-wrap gap-3 mb-0"
            style={{ minHeight: "80px" }}
          >
            {pictureUrls.map((url, index) => (
              <div
                key={`${url}-${index}`}
                className="position-relative"
                style={{ width: "100px", height: "100px" }}
              >
                <img
                  src={url}
                  alt={`Gallery ${index + 1}`}
                  className="rounded border object-fit-cover w-100 h-100"
                  style={{ objectFit: "cover" }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 translate-middle"
                  style={{ padding: "2px 6px", lineHeight: 1 }}
                  onClick={() => handleRemoveImage(index)}
                  type="button"
                  title="Remove"
                >
                  <i className="bi bi-x-lg" />
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

export default GallerySectionForm
